import { gql, useMutation } from '@apollo/client';
import {
  Button,
  ButtonGroup,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
  Textarea,
} from '@apollo/orbit';
import { useFormik } from 'formik';
import React from 'react';
import * as Yup from 'yup';

import {
  graphPersistedQueriesRouteConfig,
  variantPersistedQueriesRouteConfig,
} from 'src/app/graph/routes';
import { Form as FormWrapper } from 'src/components/common/form/Form';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { useRouteParams } from 'src/hooks/useRouteParams';
import {
  CreatePersistedQueryListMutation,
  CreatePersistedQueryListMutationVariables,
} from 'src/lib/graphqlTypes/types';
import { DocsRouter } from 'src/lib/routers';

import { persistedQueryListDetailsFragment } from '../../PersistedQueryListDetailsFragment';
import { PersistedQueryListConfigHeader } from '../PersistedQueryListConfigHeader';
import {
  PersistedQueryListConfigStep,
  PersistedQueryListConfigWorkflow,
} from '../steps';

export interface PersistedQueryListMetaModalState {
  graphId?: string;
  name?: string;
  description?: string;
}

interface Props {
  isOpen: boolean;
  state: PersistedQueryListMetaModalState | null;
  onComplete: ({
    name,
    description,
    listId,
  }: {
    name: string;
    description?: string;
    listId: string;
  }) => void;
  onClose: () => void;
}

const pqNameMinLength = 1;
const pqNameMaxLength = 128;
const pqDescriptionMaxLength = 4000;

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .required('Please provide a name for this list.')
    .trim()
    .min(pqNameMinLength)
    .max(pqNameMaxLength, `Maximum ${pqNameMaxLength} characters`),
  description: Yup.string()
    .notRequired()
    .trim()
    .max(
      pqDescriptionMaxLength,
      `Maximum ${pqDescriptionMaxLength} characters`,
    ),
});

const createPersistedQueryListMutation = gql<
  CreatePersistedQueryListMutation,
  CreatePersistedQueryListMutationVariables
>`
  mutation CreatePersistedQueryListMutation(
    $graphId: ID!
    $name: String!
    $description: String
  ) {
    service(id: $graphId) {
      createPersistedQueryList(name: $name, description: $description) {
        ... on CreatePersistedQueryListResult {
          persistedQueryList {
            id
            ...PersistedQueryListDetailsFragment
          }
        }
        ... on PermissionError {
          message
        }
      }
    }
  }
  ${persistedQueryListDetailsFragment}
`;

export const PersistedQueryListMetaModal = ({
  isOpen,
  state,
  onComplete,
  onClose,
}: Props) => {
  const { graphId: graphIdFromUrl } = useRouteParams(
    graphPersistedQueriesRouteConfig,
    variantPersistedQueriesRouteConfig,
  );

  const [mutate, { loading: mutationLoading, error }] = useMutation(
    createPersistedQueryListMutation,
    {
      update(cache, { data: returnedData }) {
        if (
          returnedData?.service?.createPersistedQueryList.__typename ===
          'CreatePersistedQueryListResult'
        ) {
          cache.modify({
            id: cache.identify({
              __typename: 'Service',
              id: graphIdFromUrl,
            }),
            fields: {
              persistedQueryLists: (cachedPQLists) => {
                return cachedPQLists.concat({
                  ...returnedData?.service?.createPersistedQueryList,
                });
              },
            },
          });
        }
      },
      onCompleted: (returnedData) => {
        if (
          returnedData.service?.createPersistedQueryList.__typename ===
          'CreatePersistedQueryListResult'
        ) {
          const newList =
            returnedData.service.createPersistedQueryList.persistedQueryList;
          onComplete({
            listId: newList.id,
            name: newList.name,
            description: newList.description,
          });
        }
      },
    },
  );

  const {
    values,
    errors,
    touched,
    handleChange,
    setFieldTouched,
    setFieldError,
    handleSubmit,
    isSubmitting,
  } = useFormik<Yup.InferType<typeof validationSchema>>({
    initialValues: {
      name: state?.name ?? '',
      description: state?.description,
    },
    enableReinitialize: true,
    validationSchema,
    validateOnChange: false,
    validateOnBlur: false,
    async onSubmit(submitted) {
      const { name, description } = validationSchema.cast(submitted);
      await mutate({
        variables: {
          graphId: graphIdFromUrl,
          name,
          description,
        },
      });
    },
  });

  return (
    <Modal isOpen={isOpen} size="xl" onClose={onClose}>
      <ModalOverlay />
      <FormWrapper onSubmit={handleSubmit} noValidate>
        <ModalContent>
          <ModalHeader>
            <PersistedQueryListConfigHeader
              currentStep={PersistedQueryListConfigStep.MetaNameDesc}
              workflow={PersistedQueryListConfigWorkflow.create}
            />
            <ModalTitle>Create a Persisted Query List</ModalTitle>
            <ModalDescription>
              You can link this list to a variant in your graph to enforce
              safelisting or set up persisted queries.{' '}
              <Link isExternal to={DocsRouter.path('PersistedQueries')}>
                Learn more
              </Link>
            </ModalDescription>
          </ModalHeader>
          <ModalBody>
            {error && (
              <ErrorMessage>
                Something went wrong. Please refresh and try again.
              </ErrorMessage>
            )}
            <FormControl isRequired isInvalid={!!errors.name && !!touched.name}>
              <FormLabel>Name</FormLabel>
              <Input
                isDisabled={isSubmitting}
                name="name"
                className="flex"
                placeholder="e.g. internal, third-party, etc."
                autoFocus
                onChange={(event) => {
                  setFieldTouched('name');
                  setFieldError('name', undefined);
                  handleChange(event);
                }}
                value={values.name ?? ''}
              />
              <FormErrorMessage>{errors.name}</FormErrorMessage>
            </FormControl>
            <FormControl
              isInvalid={!!errors.description && !!touched.description}
            >
              <FormLabel>Description</FormLabel>
              <Textarea
                name="description"
                id="description"
                onChange={(event) => {
                  setFieldTouched('description');
                  handleChange(event);
                }}
                placeholder="Help your team understand how this list is used."
                value={values.description ?? undefined}
                isDisabled={isSubmitting}
              />
              {!errors.description &&
                (!touched.description ? (
                  <FormHelperText>&nbsp;</FormHelperText>
                ) : (
                  <FormHelperText>
                    {pqDescriptionMaxLength - (values.description?.length ?? 0)}{' '}
                    characters remaining
                  </FormHelperText>
                ))}
              <FormErrorMessage>{errors.description}</FormErrorMessage>
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <ButtonGroup>
              <Button
                variant="secondary"
                type="button"
                onClick={onClose}
                isDisabled={mutationLoading}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                isDisabled={mutationLoading}
                isLoading={mutationLoading}
                loadingText="Create"
              >
                Create
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </FormWrapper>
    </Modal>
  );
};
